import { useField } from '@framework/form/field/useField.tsx';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useWidgetRootContext } from '../../../../../tree/context/WidgetRootContext.tsx';
import { TreeNavigatorLogic } from '../../../../../tree/navigation/TreeNavigatorLogic.ts';
import { type IRootWidgetIdentifier } from '../../../../../tree/references/TreeReferencesGlobalContext.tsx';
import { materializeReferencePath } from '../../../../expressions/evaluate/materializeReferencePath.ts';
import { type IReference } from '../../../../expressions/references/IReference.ts';
import { type IRepeatableRangeRuleOptions } from '../../../../rules/repository/repeatable-range/RepeatableRange.rule.ts';
import { useAllRootsContext } from '../../../../sections/hooks/AllRootsContext.tsx';
import { WidgetType } from '../../../Widget.ts';
import { type IRootWidget } from '../../containers/root/IRootWidget.ts';
import { useGetRepeatableChildrenDefault } from '../repeatable/hooks/useGetRepeatableChildrenDefault.ts';
import { type IRepeatableWidget } from '../repeatable/IRepeatableWidget.ts';

/**
 * Estrae le regole di min-max numero ripetizioni da un widget di tipo Repeatable List
 */
function getRepeatableRangeRule(widget: IRepeatableWidget) {
  const rule = widget.rules?.repeatableRange as
    | IRepeatableRangeRuleOptions
    | undefined;
  if (!rule) return { min: undefined, max: undefined };
  return {
    min: rule.min == null ? undefined : rule.min,
    max: rule.max == null ? undefined : rule.max,
  };
}

/**
 * Ritorna il codice della root dal path di una reference.
 * NB: il codice è presente solo se il componente si trova in una root differente
 * da quella del widget di partenza.
 */
function getRootCodeFromReferencePath(reference: IReference) {
  return reference.path.find(p => p.direction === 'root')?.code;
}

/**
 * Ritorna il widget di tipo Repeatable List a partire dal suo id
 * andandolo a recuperare dalla lista di tutte le root (sezioni)
 */
function getRepeatableConfigFromRoots(
  allRoots: IRootWidgetIdentifier[],
  rootId: string,
  listId: string,
) {
  const listRoot = allRoots.find(r => r.id === rootId);
  if (!listRoot) return null;
  return getRepeatableConfigFromRoot(listRoot.root, listId);
}

/**
 * Ritorna il widget di tipo Repeatable List a partire dal suo id
 * andandolo a recuperare dalla root passata come parametro
 */
function getRepeatableConfigFromRoot(
  root: IRootWidget | null | undefined,
  listId: string,
) {
  if (!root) return null;
  const listWidget = TreeNavigatorLogic.byId(root, listId);
  if (!listWidget || listWidget.type !== WidgetType.Repeatable) return null;
  return listWidget;
}

const NO_PATH_FOUND = '<no-path-found>';

export function useRepeatableListEditor(
  name: string,
  listReference: IReference | undefined,
) {
  const { root } = useWidgetRootContext();
  const { allRoots } = useAllRootsContext();

  // Path per il recupero del valore del widget lista ripetibile
  const path = useMemo(() => {
    if (!listReference) return NO_PATH_FOUND;
    return materializeReferencePath(name, listReference);
  }, [listReference, name]);

  const field = useField<Array<any>>(path);
  const values = useWatch({ name: path });

  // Funzione che calcola il default a partire dallo schema indicato da path
  const getDefault = useGetRepeatableChildrenDefault(path);

  // Recupero la configurazione del widget lista ripetibile
  // per impostare il valore di default dei nuovi elementi
  const itemRangeRuleValues = useMemo(() => {
    if (!listReference) return { min: undefined, max: undefined };
    const rootId = getRootCodeFromReferencePath(listReference);
    let listWidget: any;
    if (rootId) {
      listWidget = getRepeatableConfigFromRoots(
        allRoots,
        rootId,
        listReference.id,
      );
    } else {
      listWidget = getRepeatableConfigFromRoot(root, listReference.id);
    }

    if (!listWidget) return { min: undefined, max: undefined };
    return getRepeatableRangeRule(listWidget);
  }, [allRoots, listReference, root]);

  /**
   * Aggiunge uns singolo elemento alla lista ripetibile
   */
  const handleAdd = useCallback(() => {
    if (!listReference) return;
    const actualValue = field.getValue();
    const actualLength = actualValue?.length ?? 0;

    if (
      itemRangeRuleValues.max != null &&
      actualLength >= itemRangeRuleValues.max
    ) {
      return;
    }

    field.setValue([...(actualValue ?? []), getDefault()]);
  }, [field, getDefault, itemRangeRuleValues.max, listReference]);

  const handleRemove = useCallback(
    (index: number) => {
      if (!listReference) return;
      const actualValue = field.getValue();
      const actualLength = actualValue?.length ?? 0;

      if (
        itemRangeRuleValues.min != null &&
        actualLength <= itemRangeRuleValues.min
      ) {
        return;
      }

      field.setValue(actualValue?.filter((_, i) => i !== index) ?? []);
    },
    [field, itemRangeRuleValues.min, listReference],
  );

  return {
    field,
    values,
    getDefaultValue: getDefault,
    itemRangeRuleValues,
    handleAdd,
    handleRemove,
  };
}
